'use client';
import { useState, ChangeEvent, FormEvent, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import appLinks from "@/../config/appLinks.json"; // Import app links

// Define Turnstile types for TypeScript
declare global {
  interface Window {
    turnstile: {
      render: (container: string, options: {
        sitekey: string;
        callback: (token: string) => void;
        'refresh-expired'?: 'auto' | 'manual';
      }) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

// API endpoint
const API_ENDPOINT = 'https://meetminutes-contact-api.yr-1d5.workers.dev';

const ContactPage = () => {
  const [formState, setFormState] = useState({
    fullName: '',
    email: '',
    phone: '',
    queryType: '',
    message: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [showQueryDropdown, setShowQueryDropdown] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);

  // Load Turnstile script
  useEffect(() => {
    const loadScript = () => {
      if (document.getElementById('turnstile-script')) {
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.id = 'turnstile-script';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    };
    
    loadScript();
  }, []);

  // Initialize Turnstile widget
  useEffect(() => {
    let widgetId: string | null = null;
    
    const initTurnstile = () => {
      if (!window.turnstile) {
        return false;
      }
      
      try {
        widgetId = window.turnstile.render('#turnstile-container', {
          sitekey: '0x4AAAAAABPblnyTKiuPFbQZ',
          callback: (token) => setTurnstileToken(token),
          'refresh-expired': 'auto'
        });
        
        if (widgetId) {
          setTurnstileWidgetId(widgetId);
          return true;
        }
      } catch (error) {
        console.error('Failed to initialize Turnstile:', error);
      }
      
      return false;
    };
    
    const checkAndInit = () => {
      if (window.turnstile) {
        initTurnstile();
      } else {
        setTimeout(checkAndInit, 300);
      }
    };
    
    const timerId = setTimeout(checkAndInit, 500);
    
    return () => {
      clearTimeout(timerId);
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          // Ignore errors during cleanup
        }
      }
    };
  }, []);

  // Refresh Turnstile function for error cases
  const refreshTurnstile = () => {
    if (!turnstileWidgetId || !window.turnstile) return;
    
    try {
      window.turnstile.reset(turnstileWidgetId);
    } catch {
      console.error('Failed to reset Turnstile widget');
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleQueryTypeChange = (query: string) => {
    setFormState((prev) => ({
      ...prev,
      queryType: query,
    }));
    setShowQueryDropdown(false);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!turnstileToken) {
      setSubmitError('Please complete the captcha verification first');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitError('');

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin,
        },
        mode: 'cors',
        body: JSON.stringify({
          ...formState,
          turnstileToken: turnstileToken
        }),
      });
      
      if (!response.ok) {
        let errorText = `Error ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorText += ` - ${errorData.message}`;
          }
        } catch {
          // If we can't parse the error response, just use the status
        }
        
        throw new Error(errorText);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setSubmitMessage('Thank you for your message. Our team will reach out to you within 24 hours.');
        setFormSubmitted(true);
      } else {
        if (data.message && data.message.includes('CAPTCHA')) {
          setSubmitError('CAPTCHA verification failed. Please try again.');
          setTurnstileToken('');
          refreshTurnstile();
        } else {
          setSubmitError(data.message || 'Failed to submit the form. Please try again.');
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage = error instanceof Error ? error.message : 'A network error occurred. Please try again later.';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
      if ((submitMessage || submitError) && messageRef.current) {
        messageRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <section className="py-20 bg-[#0c0c0c] text-white">
      <div className="container mx-auto px-4 max-w-3xl">
        <h1 className="text-4xl md:text-5xl font-bold mb-12 text-center">Get in touch.</h1>

        <div className="max-w-xl mx-auto flex flex-col md:flex-row justify-between items-center mb-16 space-y-6 md:space-y-0">
          <div className="text-center md:text-left">
            <p className="text-gray-400 mb-1">You can reach us anytime via</p>
            <a href={`mailto:${appLinks.contactEmail}`} className="text-blue-400 hover:text-blue-300 text-lg font-medium">
              {appLinks.contactEmail} 
            </a>
          </div>
          <div className="text-gray-400 mx-4">or</div>
          <div className="text-center md:text-left">
            <p className="text-white mb-2">Schedule a Meeting</p>
            <a
              href={appLinks.contactBookingLink}
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full text-sm font-medium transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              Schedule Now
            </a>
          </div>
        </div>

        <h2 className="text-2xl font-semibold mb-8 text-center">Got any query?</h2>

        {!formSubmitted ? (
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-6 max-w-xl mx-auto">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-400 mb-1">
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formState.fullName}
                onChange={handleInputChange}
                required
                className="w-full bg-[#333333] border border-[#444444] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                placeholder="Jane Smith"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formState.email}
                onChange={handleInputChange}
                required
                className="w-full bg-[#333333] border border-[#444444] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-400 mb-1">
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formState.phone}
                onChange={handleInputChange}
                className="w-full bg-[#333333] border border-[#444444] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                placeholder="+1 (555) 123-4567"
              />
            </div>
            
            <div>
              <label htmlFor="queryType" className="block text-sm font-medium text-gray-400 mb-1">
                Query
              </label>
              <div className="relative">
                <button
                  type="button"
                  className="w-full flex items-center justify-between bg-[#333333] border border-[#444444] rounded-lg px-4 py-3 text-left text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                  onClick={() => setShowQueryDropdown(!showQueryDropdown)}
                >
                  <span>{formState.queryType || 'Select...'}</span>
                  <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${showQueryDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showQueryDropdown && (
                  <div className="absolute z-10 mt-1 w-full rounded-md bg-[#333333] border border-[#444444] shadow-lg">
                    <div className="py-1">
                      {[
                        'Sales/Pricing', 
                        'Technical Support', 
                        'Product Feedback', 
                        'Marketing/PR', 
                        'Careers', 
                        'Partnerships', 
                        'Other'
                      ].map((query) => (
                        <button
                          key={query}
                          type="button"
                          className="block w-full px-4 py-2 text-left text-white hover:bg-[#444444]"
                          onClick={() => handleQueryTypeChange(query)}
                        >
                          {query}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-400 mb-1">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formState.message}
                onChange={handleInputChange}
                required
                rows={4}
                className="w-full bg-[#333333] border border-[#444444] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 resize-none"
                placeholder="Please describe your query..."
              ></textarea>
            </div>

            {/* Turnstile container - centered */}
            <div 
              ref={turnstileContainerRef}
              id="turnstile-container" 
              className="my-4 flex justify-center"
            ></div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg px-6 py-3 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
            
            {/* Message container */}
            <div ref={messageRef}>
              {submitMessage && (
                <div className="mt-6 rounded-md bg-green-900/30 p-4 text-center text-green-400 max-w-xl mx-auto shadow-lg border border-green-600/20">
                  {submitMessage}
                </div>
              )}

              {submitError && (
                <div className="mt-6 rounded-md bg-red-900/30 p-4 text-center text-red-400 max-w-xl mx-auto shadow-lg border border-red-600/20">
                  {submitError}
                </div>
              )}
            </div>
          </form>
        ) : (
          <div className="space-y-6 max-w-xl mx-auto text-center">
            <div className="mt-6 rounded-md bg-green-900/30 p-6 text-center text-green-400 max-w-xl mx-auto shadow-lg border border-green-600/20">
              <h3 className="text-xl font-medium mb-3">Thank you for your message!</h3>
              <p className="mb-6">Our team will reach out to you within 24 hours.</p>
              <p className="text-sm text-gray-400">Refresh the page to submit another query.</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ContactPage;
